import React from 'react';
import { Hash, Search, PanelLeftClose, PanelLeftOpen, SquarePen } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ConversaItem, GruposDeConversas } from './conversationList';
import { Avatar } from './NovaConversaDireta';

interface ChatConversationListProps {
  grupos: GruposDeConversas;
  termo: string;
  onTermoChange: (termo: string) => void;
  /** `ConversaItem.chave` da conversa aberta. */
  chaveAtiva: string | null;
  onSelecionar: (conversa: ConversaItem) => void;
  /** Abre o seletor de pessoas para iniciar uma conversa privada. */
  onNovaConversa: () => void;
  carregando: boolean;
  colapsada: boolean;
  onAlternarColapso: () => void;
  /** Botão de colapsar só faz sentido quando a conversa aparece ao lado. */
  permiteColapsar: boolean;
}

const quando = (iso: string | null): string => {
  if (!iso) return '';
  try {
    return formatDistanceToNowStrict(new Date(iso), { locale: ptBR });
  } catch {
    return '';
  }
};

const ItemDeConversa: React.FC<{
  conversa: ConversaItem;
  ativa: boolean;
  onClick: () => void;
}> = ({ conversa, ativa, onClick }) => (
  <li>
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-2 py-2 rounded-md flex items-start gap-2 transition-colors ${
        ativa ? 'bg-coral-soft text-coral-deep' : 'hover:bg-gray-100 text-gray-700'
      }`}
      title={conversa.nome}
    >
      {conversa.tipo === 'direta' ? (
        <span className="mt-0.5">
          <Avatar nome={conversa.nome} url={conversa.avatarUrl} tamanho={16} />
        </span>
      ) : (
        <Hash className={`w-4 h-4 mt-0.5 flex-shrink-0 ${ativa ? 'text-coral-deep' : 'text-gray-400'}`} />
      )}
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span
            className={`flex-1 truncate text-sm ${
              conversa.naoLidas > 0 ? 'font-semibold text-gray-900' : 'font-medium'
            }`}
          >
            {conversa.nome}
          </span>
          {conversa.ultimaMensagemEm && (
            <span className="text-[10px] text-gray-400 flex-shrink-0">
              {quando(conversa.ultimaMensagemEm)}
            </span>
          )}
        </span>
        {conversa.previa && (
          <span className="block truncate text-xs text-gray-500 mt-0.5">{conversa.previa}</span>
        )}
      </span>
      {conversa.naoLidas > 0 && (
        <span className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-nk-error text-white text-[10px] font-bold flex items-center justify-center">
          {conversa.naoLidas > 99 ? '99+' : conversa.naoLidas}
        </span>
      )}
    </button>
  </li>
);

const ChatConversationList: React.FC<ChatConversationListProps> = ({
  grupos,
  termo,
  onTermoChange,
  chaveAtiva,
  onSelecionar,
  onNovaConversa,
  carregando,
  colapsada,
  onAlternarColapso,
  permiteColapsar,
}) => {
  const totalNaoLidas = grupos.naoLidas.reduce((soma, c) => soma + c.naoLidas, 0);
  const vazia =
    grupos.naoLidas.length === 0 && grupos.diretas.length === 0 && grupos.projetos.length === 0;

  const itens = (conversas: ConversaItem[]) =>
    conversas.map(conversa => (
      <ItemDeConversa
        key={conversa.chave}
        conversa={conversa}
        ativa={conversa.chave === chaveAtiva}
        onClick={() => onSelecionar(conversa)}
      />
    ));

  if (colapsada) {
    return (
      <div className="chat-conversation-list collapsed">
        <button
          type="button"
          onClick={onAlternarColapso}
          className="p-2 rounded hover:bg-gray-200 text-gray-600"
          title="Mostrar conversas"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
        {totalNaoLidas > 0 && (
          <span className="mt-2 min-w-[18px] h-[18px] px-1 rounded-full bg-nk-error text-white text-[10px] font-bold flex items-center justify-center">
            {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="chat-conversation-list">
      <div className="chat-conversation-list-header">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={termo}
            onChange={e => onTermoChange(e.target.value)}
            placeholder="Buscar conversas"
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
          />
        </div>
        <button
          type="button"
          onClick={onNovaConversa}
          className="p-1.5 rounded hover:bg-gray-200 text-gray-500 flex-shrink-0"
          title="Nova conversa privada"
        >
          <SquarePen className="w-4 h-4" />
        </button>
        {permiteColapsar && (
          <button
            type="button"
            onClick={onAlternarColapso}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-500 flex-shrink-0"
            title="Ocultar conversas"
          >
            <PanelLeftClose className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="chat-conversation-list-body">
        {carregando ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-coral" />
          </div>
        ) : vazia && termo ? (
          <p className="text-center text-xs text-gray-500 py-6 px-3">
            Nenhuma conversa encontrada
          </p>
        ) : (
          <>
            {grupos.naoLidas.length > 0 && (
              <section className="mb-3">
                <h4 className="chat-conversation-group-title">Não lidas</h4>
                <ul className="space-y-0.5">{itens(grupos.naoLidas)}</ul>
              </section>
            )}

            {/* Sempre visível fora da busca: é por aqui que se descobre que dá
                para conversar em particular, mesmo antes da primeira conversa. */}
            {(grupos.diretas.length > 0 || !termo) && (
              <section className="mb-3">
                <h4 className="chat-conversation-group-title">Mensagens diretas</h4>
                {grupos.diretas.length > 0 ? (
                  <ul className="space-y-0.5">{itens(grupos.diretas)}</ul>
                ) : (
                  <button
                    type="button"
                    onClick={onNovaConversa}
                    className="w-full text-left px-2 py-1.5 rounded-md text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 flex items-center gap-2"
                  >
                    <SquarePen className="w-3.5 h-3.5" />
                    Iniciar uma conversa privada
                  </button>
                )}
              </section>
            )}

            {grupos.projetos.length > 0 && (
              <section>
                <h4 className="chat-conversation-group-title">Projetos</h4>
                <ul className="space-y-0.5">{itens(grupos.projetos)}</ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatConversationList;
